import React, { useState, useRef, useEffect, useMemo } from "react"
import * as XLSX from "xlsx"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import {
  UploadCloud,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Download,
  Loader2,
  X,
  ArrowRight,
  Printer,
  RefreshCw,
  Package,
} from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { INDEX_CELL, INDEX_HEADER } from "@/components/ui/data-table"
import { ApiError, downloadFile, post, get } from "@/lib/api"
import { printWaybills } from "@/lib/printWaybills"
import { useProfileIncompleteHandler } from "@/lib/onboarding"
import { cn } from "@/lib/utils"
import { useVolumetricRule } from "@/lib/volumetricRule"
import { money, normalizePhoneVN, isValidVnPhone } from "@/lib/format"
import {
  IMPORT_FIELDS,
  aliasMapping,
  buildImportOrders,
  packageName,
  parseImportLine,
  weightUnitOf,
  type ColumnPlan,
  type ImportField,
  type MergedItem,
} from "@shoptrack/common/import-columns"
import { PricingBlocked, useOrderBlock } from '@/components/pricing/PricingNotice'

/** Một ĐƠN sẽ tạo — mỗi dòng file một đơn. */
interface ParsedRow {
  /** Khoá ổn định của đơn (dòng + cân + COD) — kết quả kiểm cước gắn theo khoá này */
  key: string
  /** Dòng của đơn trong file */
  rowNum: number
  /** Mặt hàng của dòng (cân mỗi cái = cân dòng ÷ số lượng) */
  items: MergedItem[]
  /** Số kiện — luôn 1 (mỗi dòng một kiện) */
  numberOfProducts: number
  /** Số mặt hàng chưa có trong kho — tạo mới khi nhập */
  newProducts: number
  clientOrderId?: string
  customerName: string
  customerPhone?: string
  destAddress: string
  destCity?: string
  destDistrict?: string
  destWard?: string
  productName?: string
  qty?: number
  weightGram?: number
  cod?: number
  cost?: number
  channel?: string
  note?: string
  productPrice?: number
  pickupType?: 1 | 2
  length?: number // mm
  width?: number  // mm
  height?: number // mm
  shipmentPayer?: 0 | 1
  /** Cột "Kho" — tên kho lấy hàng; trống = kho mặc định */
  warehouseName?: string
  warehouseId?: number
  /** "Shop trả" mà COD thấp hơn cước: tiền ship shop chuyển khoản trước (đơn chờ duyệt, chưa gửi BEST). */
  shipPrepay?: number
  valid: boolean
  errors: string[]
}

interface ImportResultItem {
  orderId: string
  clientOrderId: string
  sagaId: string | null
  success: boolean
  error?: string
  customerName: string
  customerPhone?: string
  destAddress: string
  cod?: number
  shipmentCode?: string | null
  /** Đơn chờ shop chuyển khoản tiền ship (QR) — admin duyệt mới gửi BEST. */
  shipPayment?: { amount: number; transferContent: string }
  /** Mã đơn trùng đơn còn hiệu lực — không tạo mới */
  existing?: boolean
}

interface ImportOrdersModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

/** `withDims` = false khi Super Admin tắt quy đổi thể tích — file mẫu không có cột Dài / Rộng / Cao. */
export function downloadOrderTemplate(type: "xlsx" | "csv" = "xlsx", withDims = true) {
  const headers = [
    {
      "Mã đơn": "VD-001",
      "Tên khách hàng": "Nguyễn Văn An",
      "Số điện thoại": "0912345678",
      "Địa chỉ giao hàng": "123 Nguyễn Huệ, Phường Bến Nghé",
      "Tỉnh/Thành phố": "Hồ Chí Minh",
      "Quận/Huyện": "Quận 1",
      "Phường/Xã": "Phường Bến Nghé",
      "Tên sản phẩm": "Áo sơ mi Oxford",
      "Số lượng": 1,
      "Khối lượng (gram)": 300,
      "Tiền thu hộ COD": 350000,
      "Giá vốn": 150000,
      "Khai giá bảo hiểm": 0,
      "Người trả cước (0: Shop, 1: Khách)": 0,
      "Hình thức lấy (1: Bưu cục, 2: Shipper)": 2,
      "Dài (cm)": 20,
      "Rộng (cm)": 15,
      "Cao (cm)": 10,
      "Kênh bán": "POS",
      "Kho": "",
      "Ghi chú": "Cho kiểm hàng trước khi nhận",
    },
    {
      "Mã đơn": "VD-002",
      "Tên khách hàng": "Trần Thị Mai",
      "Số điện thoại": "0987654321",
      "Địa chỉ giao hàng": "45 Phố Huế, Phường Hàng Bài",
      "Tỉnh/Thành phố": "Hà Nội",
      "Quận/Huyện": "Quận Hoàn Kiếm",
      "Phường/Xã": "Phường Hàng Bài",
      "Tên sản phẩm": "Váy hoa nhí",
      "Số lượng": 1,
      "Khối lượng (gram)": 400,
      "Tiền thu hộ COD": 420000,
      "Giá vốn": 180000,
      "Khai giá bảo hiểm": 0,
      "Người trả cước (0: Shop, 1: Khách)": 0,
      "Hình thức lấy (1: Bưu cục, 2: Shipper)": 2,
      "Dài (cm)": 25,
      "Rộng (cm)": 20,
      "Cao (cm)": 5,
      "Kênh bán": "TikTok",
      "Ghi chú": "Giao giờ hành chính",
    },
    {
      "Mã đơn": "VD-003",
      "Tên khách hàng": "Lê Hoàng Long",
      "Số điện thoại": "0903123456",
      "Địa chỉ giao hàng": "78 Lê Duẩn, Phường Thạch Thang",
      "Tỉnh/Thành phố": "Đà Nẵng",
      "Quận/Huyện": "Quận Hải Châu",
      "Phường/Xã": "Phường Thạch Thang",
      "Tên sản phẩm": "Concha · KingDom",
      "Số lượng": 1,
      "Khối lượng (gram)": 500,
      "Tiền thu hộ COD": 700000,
      "Giá vốn": 300000,
      "Khai giá bảo hiểm": 0,
      "Người trả cước (0: Shop, 1: Khách)": 0,
      "Hình thức lấy (1: Bưu cục, 2: Shipper)": 2,
      "Dài (cm)": 30,
      "Rộng (cm)": 20,
      "Cao (cm)": 15,
      "Kênh bán": "FB Live",
      "Ghi chú": "Gọi trước 15p",
    },
  ]
  const DIM_COLS = ["Dài (cm)", "Rộng (cm)", "Cao (cm)"]
  const ws = XLSX.utils.json_to_sheet(withDims ? headers : headers.map((h) => Object.fromEntries(Object.entries(h).filter(([k]) => !DIM_COLS.includes(k)))))
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "Mau_Don_Hang")
  if (type === "xlsx") {
    XLSX.writeFile(wb, "Mau_nhap_don_hang_ShopTrack.xlsx")
  } else {
    XLSX.writeFile(wb, "Mau_nhap_don_hang_ShopTrack.csv")
  }
}

export function ImportOrdersModal({ open, onClose, onSuccess }: ImportOrdersModalProps) {
  // Quy đổi thể tích tắt (Super Admin) → cột Dài / Rộng / Cao trong file bỏ qua, không gửi
  const volumetricRule = useVolumetricRule()
  // Chưa có bảng giá (mình hoặc cấp trên) → không nhập được (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.5)
  const priceBlock = useOrderBlock()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const handleProfileIncomplete = useProfileIncompleteHandler()
  const [fileName, setFileName] = useState<string | null>(null)
  // File đã đọc (dòng thô) + bảng ghép cột (AI đọc tên cột, lỗi thì khớp theo tên) → đơn (rows, tính lại
  // khi đổi kho / sản phẩm)
  const [sheet, setSheet] = useState<{ rows: Record<string, unknown>[]; rowNums: number[]; headers: string[] } | null>(null)
  const [plan, setPlan] = useState<(ColumnPlan & { source: "ai" | "rules" }) | null>(null)
  const [readingColumns, setReadingColumns] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [autoCreateShipment, setAutoCreateShipment] = useState(true)
  const [importing, setImporting] = useState(false)
  // Đang chuẩn bị vận đơn vừa tạo: in hay tải
  const [busy, setBusy] = useState<"print" | "download" | null>(null)
  const [activeFilter, setActiveFilter] = useState<"all" | "valid" | "invalid">("all")

  // Load shop products for matching and validation
  const { data: productData } = useQuery({
    queryKey: ["products-active"],
    queryFn: () => get<{ products: Array<{ id: string; sku: string; name: string; price: number; cost: number; weightGram: number; stock: number }> }>("/products?active=true"),
    staleTime: 0,
  })

  // Load banks and warehouse to validate requirements
  const { data: bankData } = useQuery({
    queryKey: ["banks"],
    queryFn: () => get<{ banks: any[] }>("/banks"),
    staleTime: 1000 * 60 * 5,
  })
  const hasBankAccount = (bankData?.banks?.length ?? 0) > 0

  const { data: warehouseData } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => get<{ warehouses: Array<{ id: number; name: string; isDefault: boolean }> }>("/settings/warehouses"),
    staleTime: 1000 * 60 * 5,
  })
  const warehouses = warehouseData?.warehouses ?? []
  const hasWarehouseAddress = warehouses.length > 0

  // Step 2: Live tracking progress state
  const [step, setStep] = useState<"upload" | "progress">("upload")
  const [importResults, setImportResults] = useState<ImportResultItem[]>([])
  const pollingRef = useRef<number | null>(null)

  const rows = useMemo<ParsedRow[]>(() => {
    if (!sheet || !plan) return []
    const shopProducts = productData?.products || []
    const inStock = (it: MergedItem) =>
      shopProducts.some((p) => (it.sku && p.sku.toLowerCase() === it.sku.toLowerCase()) || p.name.trim().toLowerCase() === it.name.toLowerCase())
    const lines = sheet.rows
      .map((r, i) => parseImportLine(r, sheet.rowNums[i]!, plan))
      // Dòng ví dụ trong file mẫu (mã "VD-…") — quên xoá cũng không tạo nhầm đơn thật
      .filter((l) => !/^VD-\d+$/i.test(l.orderCode))
    // Mỗi dòng một đơn (sản phẩm gộp vào một ô) — không gom nhiều dòng
    return buildImportOrders(lines).map((o): ParsedRow => {
      const h = o.line
      const errors: string[] = []
      const warehouseName = h.warehouse
      const warehouse = warehouseName ? warehouses.find((w) => w.name.trim().toLowerCase() === warehouseName.toLowerCase()) : undefined
      if (o.sameCodeRows.length) errors.push(`Mã đơn "${h.orderCode}" trùng dòng ${o.sameCodeRows.slice(0, 5).join(", ")}${o.sameCodeRows.length > 5 ? "…" : ""}`)
      if (warehouseName && !warehouse) errors.push(`Không có kho "${warehouseName}"`)
      if (h.journeyType && h.journeyType !== "1") errors.push("Chưa hỗ trợ đơn thu hồi / đổi hàng")
      if (!h.customerName) errors.push("Thiếu tên khách hàng")
      if (!h.address) errors.push("Thiếu địa chỉ nhận")
      if (h.customerPhone && !isValidVnPhone(h.customerPhone)) errors.push("Số điện thoại không đúng định dạng di động VN (10 số)")
      if (autoCreateShipment) {
        if (!h.customerPhone) errors.push("Thiếu SĐT người nhận")
        if (!h.city) errors.push("Thiếu Tỉnh / Thành phố nhận hàng")
        if (!h.ward) errors.push("Thiếu Phường / Xã nhận hàng")
      }
      if (h.badNumbers.length) errors.push(`Không đọc được số ở cột ${h.badNumbers.join(", ")}`)
      return {
        key: `${h.rowNum}|${o.weightGram}|${o.cod}`,
        rowNum: h.rowNum,
        items: o.items,
        numberOfProducts: 1,
        newProducts: o.items.filter((it) => !inStock(it)).length,
        clientOrderId: h.orderCode || undefined,
        customerName: h.customerName,
        customerPhone: h.customerPhone ? normalizePhoneVN(h.customerPhone) : undefined,
        destAddress: h.address,
        destCity: h.city || undefined,
        destDistrict: h.district || undefined,
        destWard: h.ward || undefined,
        productName: packageName(o.items),
        qty: o.qty,
        weightGram: o.weightGram,
        cod: o.cod,
        cost: o.cost,
        channel: h.channel || "Excel/CSV",
        note: o.note || undefined,
        productPrice: o.declaredValue || undefined,
        pickupType: h.pickupType,
        shipmentPayer: h.shipmentPayer,
        ...(volumetricRule.enabled ? { length: o.dims?.lengthMm, width: o.dims?.widthMm, height: o.dims?.heightMm } : {}),
        warehouseName: warehouseName || undefined,
        warehouseId: warehouse?.id,
        valid: errors.length === 0,
        errors,
      }
    })
  }, [sheet, plan, productData, warehouses, autoCreateShipment, volumetricRule.enabled])

  // ─── Cước theo bảng giá cho dòng "Shop trả" có tạo vận đơn ───────────────────────────────
  // COD thấp hơn cước → đơn đi luồng chuyển khoản trước tiền ship (QR, chờ duyệt — máy chủ quyết định
  // theo cùng quy tắc importNeedsShipPrepay), không còn là lỗi. Dòng "Khách trả" không cần: máy chủ tự
  // cộng cước vào COD. Cước chỉ phụ thuộc kho + nơi nhận + cân / kích thước (không gọi BEST ước tính) →
  // hỏi /shipping/quote MỘT lần cho mỗi nhóm giống nhau: file 1665 đơn cùng địa chỉ chỉ vài lần gọi
  // (trước gọi từng đơn → vượt giới hạn 100 lần/phút, hơn nghìn đơn báo lỗi). Bị 429 thì chờ rồi thử lại.
  const quoteKey = (r: ParsedRow) =>
    [r.warehouseId ?? "", r.destCity ?? "", r.destDistrict ?? "", r.destWard ?? "", r.weightGram || 500, r.length ?? "", r.width ?? "", r.height ?? ""].join("|")
  const [feeChecks, setFeeChecks] = useState<Record<string, { sellerPrice?: number; error?: string }>>({})
  const needsFeeCheck = (r: ParsedRow) => autoCreateShipment && r.valid && (r.shipmentPayer ?? 0) === 0
  useEffect(() => {
    const todo = new Map<string, ParsedRow>()
    for (const r of rows) if (needsFeeCheck(r) && !feeChecks[quoteKey(r)] && !todo.has(quoteKey(r))) todo.set(quoteKey(r), r)
    if (!todo.size) return
    let cancelled = false
    const queue = [...todo.entries()]
    const worker = async () => {
      for (let next = queue.shift(); next && !cancelled; next = queue.shift()) {
        const [key, row] = next
        for (let attempt = 0; ; attempt++) {
          try {
            const q = await post<{ sellerPrice: number }>("/shipping/quote", {
              destCity: row.destCity ?? "",
              destDistrict: row.destDistrict ?? null,
              destWard: row.destWard ?? null,
              weightGram: row.weightGram || 500,
              length: row.length ?? null,
              width: row.width ?? null,
              height: row.height ?? null,
              warehouseId: row.warehouseId ?? null,
              cod: 0,
              withBestEstimate: false,
            })
            if (!cancelled) setFeeChecks((m) => ({ ...m, [key]: { sellerPrice: q.sellerPrice } }))
            break
          } catch (e) {
            // Giới hạn tần suất: chờ đúng khoảng máy chủ báo (tối đa 20 giây) rồi thử lại — không đánh lỗi đơn
            if (e instanceof ApiError && e.statusCode === 429 && attempt < 10 && !cancelled) {
              const wait = Number((e.details as { retryAfterSeconds?: number } | undefined)?.retryAfterSeconds) || 10
              await new Promise((r) => setTimeout(r, Math.min(wait, 20) * 1000))
              continue
            }
            if (!cancelled) setFeeChecks((m) => ({ ...m, [key]: { error: (e as Error).message || "lỗi không xác định" } }))
            break
          }
        }
      }
    }
    void Promise.all(Array.from({ length: Math.min(2, queue.length) }, worker))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, autoCreateShipment])
  const pendingFeeChecks = rows.filter((r) => needsFeeCheck(r) && !feeChecks[quoteKey(r)]).length
  const feeCheckTotal = rows.filter(needsFeeCheck).length

  /** Dòng sau khi gộp kết quả cước: không tính được cước → lỗi; COD < cước → chờ chuyển tiền ship. */
  const checkedRows = useMemo(
    () =>
      rows.map((r) => {
        if (!needsFeeCheck(r)) return r
        const c = feeChecks[quoteKey(r)]
        if (!c) return r
        if (c.error) return { ...r, valid: false, errors: [`Không tính được cước ship: ${c.error}`, ...r.errors] }
        if (c.sellerPrice != null && (r.cod ?? 0) < c.sellerPrice) return { ...r, shipPrepay: c.sellerPrice }
        return r
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, feeChecks, autoCreateShipment],
  )

  const validRows = checkedRows.filter((r) => r.valid)
  const invalidRows = checkedRows.filter((r) => !r.valid)
  const prepayRows = validRows.filter((r) => r.shipPrepay != null)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    processFile(file)
  }

  function processFile(file: File) {
    setFileName(file.name)
    const reader = new FileReader()

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer)
        const workbook = XLSX.read(data, { type: "array" })
        const firstSheetName = workbook.SheetNames[0]
        if (!firstSheetName) {
          toast.error("File Excel không có trang tính nào")
          return
        }
        const worksheet = workbook.Sheets[firstSheetName]
        if (!worksheet) {
          toast.error("Không thể đọc trang tính")
          return
        }
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" }) as Record<string, unknown>[]
        if (!json.length) {
          toast.error("File rỗng hoặc không có dữ liệu")
          return
        }
        // Tên cột lấy đúng như khoá của dòng (có file giữ dấu cách thừa); bỏ cột không tên
        const headers = [...new Set(json.slice(0, 50).flatMap((r) => Object.keys(r)))].filter((h) => h.trim() && !h.startsWith("__EMPTY"))
        setFeeChecks({})
        setSheet({ rows: json, rowNums: json.map((_, i) => i + 2), headers })
        setPlan(null)
        setReadingColumns(true)
        try {
          // AI chỉ đọc TÊN CỘT (không gửi dữ liệu đơn); lỗi → khớp theo tên ở máy chủ
          const r = await post<{ mapping: ColumnPlan["mapping"]; weightUnit: "g" | "kg"; source: "ai" | "rules" }>("/imports/columns", { headers: headers.slice(0, 80) })
          setPlan({ mapping: r.mapping, weightUnit: r.weightUnit, source: r.source })
        } catch {
          const mapping = aliasMapping(headers)
          setPlan({ mapping, weightUnit: weightUnitOf(mapping.weight), source: "rules" })
        } finally {
          setReadingColumns(false)
        }
        toast.success(`Đã đọc ${json.length} dòng từ file`)
      } catch (err) {
        toast.error("Lỗi khi đọc file: " + (err as Error).message)
      }
    }

    reader.readAsArrayBuffer(file)
  }

  async function handleImport() {
    if (!validRows.length) {
      toast.error("Không có dòng hợp lệ")
      return
    }

    if (!hasBankAccount) {
      toast.error("Chưa có tài khoản ngân hàng")
      return
    }

    if (autoCreateShipment && !hasWarehouseAddress) {
      toast.error("Chưa có kho lấy hàng")
      return
    }

    setImporting(true)
    try {
      const payload = {
        orders: validRows.map((r) => ({
          clientOrderId: r.clientOrderId,
          customerName: r.customerName,
          customerPhone: r.customerPhone,
          destAddress: r.destAddress,
          destCity: r.destCity || "",
          destDistrict: r.destDistrict || null,
          destWard: r.destWard || "",
          productName: r.productName,
          qty: r.qty,
          weightGram: r.weightGram,
          cod: r.cod,
          cost: r.cost,
          channel: r.channel,
          note: r.note,
          productPrice: r.productPrice,
          pickupType: r.pickupType,
          shipmentPayer: r.shipmentPayer,
          warehouseId: r.warehouseId,
          length: r.length,
          width: r.width,
          height: r.height,
          numberOfProducts: r.numberOfProducts,
          createShipment: autoCreateShipment,
          // Mặt hàng của đơn — sản phẩm chưa có trong kho máy chủ tạo mới
          items: r.items.map((it) => ({
            name: it.name,
            ...(it.sku ? { sku: it.sku } : {}),
            qty: it.qty,
            unitWeightGram: it.unitWeightGram,
            unitCost: it.unitCost,
            ...(it.unitPrice != null ? { unitPrice: it.unitPrice } : {}),
          })),
        })),
        autoCreateShipment,
      }

      type BatchResult = {
        total: number
        imported: number
        failed: number
        results: Array<{
          orderId: string
          clientOrderId: string
          shipmentCode?: string | null
          sagaId: string | null
          success: boolean
          error?: string
          shipPayment?: { amount: number; transferContent: string }
          existing?: boolean
        }>
      }
      // Máy chủ nhận ≤ 500 đơn / lần (và giới hạn dung lượng) — chia lô 200 đơn, gộp kết quả
      const res: BatchResult = { total: 0, imported: 0, failed: 0, results: [] }
      for (let i = 0; i < payload.orders.length; i += 200) {
        const part = await post<BatchResult>("/orders/batch-import", { ...payload, orders: payload.orders.slice(i, i + 200) })
        res.total += part.total
        res.imported += part.imported
        res.failed += part.failed
        res.results.push(...part.results)
      }

      const resultItems: ImportResultItem[] = (res.results || []).map((r, i) => {
        const sourceRow = validRows[i]
        return {
          orderId: r.orderId || sourceRow?.clientOrderId || `DH-${i + 1}`,
          clientOrderId: r.clientOrderId || sourceRow?.clientOrderId || "",
          sagaId: r.sagaId,
          success: r.success,
          error: r.error,
          customerName: sourceRow?.customerName || "",
          customerPhone: sourceRow?.customerPhone,
          destAddress: sourceRow?.destAddress || "",
          cod: sourceRow?.cod,
          shipmentCode: r.shipmentCode ?? null,
          shipPayment: r.shipPayment,
          existing: r.existing,
        }
      })

      setImportResults(resultItems)
      setStep("progress")
      toast.success(`Đã xử lý ${res.imported}/${res.total} đơn hàng`)

      startPollingShipmentCodes()
    } catch (err) {
      // 409 profile.incomplete: hồ sơ đơn vị chưa đủ → báo kèm nút "Hoàn tất thông tin"
      if (!handleProfileIncomplete(err)) toast.error((err as Error).message)
    } finally {
      setImporting(false)
    }
  }

  function startPollingShipmentCodes() {
    let pollCount = 0

    const poll = async () => {
      try {
        const res = await get<{ orders: Array<{ id: string; clientOrderId?: string; shipmentCode: string | null; status: string }> }>("/orders?limit=100")
        if (res?.orders) {
          const mapById = new Map(res.orders.map((o) => [o.id, o.shipmentCode]))
          const mapByClient = new Map(res.orders.filter((o) => o.clientOrderId).map((o) => [o.clientOrderId!, o.shipmentCode]))

          setImportResults((prev) =>
            prev.map((item) => {
              const code = mapById.get(item.orderId) ?? mapByClient.get(item.clientOrderId) ?? item.shipmentCode
              if (code && code !== item.shipmentCode) {
                return { ...item, shipmentCode: code }
              }
              return item
            }),
          )
        }
      } catch {
        // ignore
      }

      pollCount++
      if (pollCount < 12) {
        pollingRef.current = window.setTimeout(poll, 2000)
      }
    }

    poll()
  }

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current)
    }
  }, [])

  // In / tải vận đơn vừa tạo, đúng thứ tự dòng file. Tải: ≤ 50 → một PDF, nhiều hơn → zip (50 vận đơn / PDF)
  async function handleWaybills(kind: "print" | "download") {
    const codes = importResults.filter((r) => !r.existing).map((r) => r.shipmentCode).filter((c): c is string => Boolean(c))
    if (!codes.length) {
      toast.error("Chưa có mã vận đơn nào")
      return
    }
    setBusy(kind)
    try {
      if (kind === "print") await printWaybills(codes)
      else {
        await downloadFile("/shipping/waybills/download", `van-don_${codes.length}.${codes.length <= 50 ? "pdf" : "zip"}`, { codes })
        toast.success(`Đã tải ${codes.length} vận đơn`)
      }
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setBusy(null)
    }
  }

  function handleReset() {
    setFileName(null)
    setSheet(null)
    setPlan(null)
    setStep("upload")
    setImportResults([])
    if (pollingRef.current) clearTimeout(pollingRef.current)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  function handleCloseModal() {
    if (step === "progress") {
      onSuccess()
    }
    handleReset()
    onClose()
  }

  const displayedRows = checkedRows.filter((r) => {
    if (activeFilter === "valid") return r.valid
    if (activeFilter === "invalid") return !r.valid
    return true
  })

  // Chỉ vận đơn của đơn vừa tạo (đơn trùng mã đã có từ trước không tính)
  const readyToPrintCount = importResults.filter((r) => Boolean(r.shipmentCode) && !r.existing).length

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleCloseModal()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0" aria-describedby={undefined}>
        <DialogHeader className="p-6 pb-4 border-b border-rule">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">
                {step === "upload" ? "Tạo đơn bằng Excel" : "Tiến trình xử lý vận đơn"}
              </DialogTitle>
            </div>
            {step === "upload" && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadOrderTemplate("xlsx", volumetricRule.enabled)} className="text-xs">
                   Mẫu Excel
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadOrderTemplate("csv", volumetricRule.enabled)} className="text-xs">
                   Mẫu CSV
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {priceBlock && step === "upload" ? (
            <PricingBlocked text={priceBlock} className="shadow-none" />
          ) : step === "upload" ? (
            /* ═════════ STEP 1: UPLOAD & PREVIEW ═════════ */
            !sheet ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  const file = e.dataTransfer.files[0]
                  if (file) processFile(file)
                }}
                className="border-2 border-dashed border-rule rounded-lg p-10 text-center hover:border-accent cursor-pointer bg-paperSoft/40 transition-colors"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                />
                <UploadCloud className="size-12 mx-auto text-accent mb-3" />
                <p className="font-medium text-ink">Chọn hoặc kéo thả file</p>
                <p className="text-xs text-muted mt-1">.xlsx, .xls, .csv</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* File Info Bar */}
                <div className="flex items-center justify-between p-3 rounded-lg border border-rule bg-paperSoft">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-sm font-medium text-ink">{fileName}</p>
                      {readingColumns ? (
                        <p className="text-xs text-inkSoft flex items-center gap-1.5">
                          <Loader2 className="size-3 animate-spin" /> AI đang đọc tên cột của file…
                        </p>
                      ) : (
                        <p className="text-xs text-muted">
                          <b>{rows.length}</b> đơn (Hợp lệ: <b className="text-ok">{validRows.length}</b>, Lỗi:{" "}
                          <b className="text-danger">{invalidRows.length}</b>)
                        </p>
                      )}
                      {prepayRows.length > 0 && (
                        <p className="text-xs text-ink">
                          <b className="text-warn">{prepayRows.length}</b> đơn chờ chuyển tiền ship
                        </p>
                      )}
                      {pendingFeeChecks > 0 && (
                        <p className="text-xs text-inkSoft flex items-center gap-1.5">
                          <Loader2 className="size-3 animate-spin" /> Đang kiểm tra cước ship {feeCheckTotal - pendingFeeChecks}/{feeCheckTotal} đơn…
                        </p>
                      )}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={handleReset} className="text-xs text-muted hover:text-danger">
                    <X className="size-4 mr-1" /> Chọn file khác
                  </Button>
                </div>

                {/* Cột đã đọc (AI / theo tên) */}
                {plan && (
                  <div className="rounded-lg border border-rule px-3 py-2 text-xs">
                    <button type="button" onClick={() => setShowColumns((v) => !v)} className="flex w-full items-center justify-between gap-2 text-left text-ink">
                      <span>
                        {plan.source === "ai" ? "AI đã đọc" : "Đã khớp theo tên"} <b>{Object.keys(plan.mapping).length}</b> cột
                        {(["customerName", "address", "productName"] as ImportField[]).filter((f) => !plan.mapping[f]).length > 0 && (
                          <span className="ml-1 text-danger">
                            · thiếu cột {(["customerName", "address", "productName"] as ImportField[]).filter((f) => !plan.mapping[f]).map((f) => IMPORT_FIELDS[f].label).join(", ")}
                          </span>
                        )}
                      </span>
                      <span className="text-accent">{showColumns ? "Ẩn" : "Xem"}</span>
                    </button>
                    {showColumns && (
                      <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
                        {(Object.keys(IMPORT_FIELDS) as ImportField[]).filter((f) => plan.mapping[f]).map((f) => (
                          <React.Fragment key={f}>
                            <dt className="text-muted">{IMPORT_FIELDS[f].label}</dt>
                            <dd className="truncate font-mono text-ink">{plan.mapping[f]!.trim()}{f === "weight" ? ` (${plan.weightUnit})` : ""}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    )}
                  </div>
                )}

                {/* Options & Filter */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2">
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={autoCreateShipment}
                        onChange={(e) => setAutoCreateShipment(e.target.checked)}
                        className="size-4 rounded accent-accent"
                      />
                      <span className="text-sm text-ink font-medium">Tự động tạo vận đơn</span>
                    </label>
                  </div>

                  <div className="flex items-center gap-1 p-1 rounded-md border border-rule bg-paperSoft text-xs">
                    <button
                      onClick={() => setActiveFilter("all")}
                      className={cn("px-2.5 py-1 rounded transition-colors", activeFilter === "all" && "bg-raise shadow-sm font-medium text-ink")}
                    >
                      Tất cả đơn ({rows.length})
                    </button>
                    <button
                      onClick={() => setActiveFilter("valid")}
                      className={cn("px-2.5 py-1 rounded transition-colors", activeFilter === "valid" && "bg-raise shadow-sm font-medium text-ok")}
                    >
                      Hợp lệ ({validRows.length})
                    </button>
                    <button
                      onClick={() => setActiveFilter("invalid")}
                      className={cn("px-2.5 py-1 rounded transition-colors", activeFilter === "invalid" && "bg-raise shadow-sm font-medium text-danger")}
                    >
                      Lỗi ({invalidRows.length})
                    </button>
                  </div>
                </div>

                {/* Preview Table */}
                <div className="border border-rule rounded-lg overflow-hidden max-h-72 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-paperSoft sticky top-0 border-b border-rule">
                      <tr className="text-left text-muted">
                        <th className="p-2.5">Dòng</th>
                        <th className="p-2.5">Trạng thái</th>
                        <th className="p-2.5">Khách hàng</th>
                        <th className="p-2.5">SĐT</th>
                        <th className="p-2.5">Địa chỉ</th>
                        <th className="p-2.5">Sản phẩm</th>
                        <th className="p-2.5 text-right">Cân</th>
                        <th className="p-2.5 text-right">COD</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-rule">
                      {displayedRows.map((r) => (
                        <tr key={r.key} className={cn("hover:bg-paperSoft/50", !r.valid && "bg-danger/5")}>
                          <td className="p-2.5 font-mono text-muted whitespace-nowrap">
                            {r.rowNum}
                          </td>
                          <td className="p-2.5">
                            {r.valid && r.shipPrepay != null ? (
                              <span className="inline-flex items-center gap-1 text-warn font-medium">
                                Chờ chuyển tiền ship {money(r.shipPrepay)}
                              </span>
                            ) : r.valid ? (
                              <span className="inline-flex items-center gap-1 text-ok font-medium">
                                 Hợp lệ
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-danger font-medium" title={r.errors.join(", ")}>
                                 {r.errors[0]}
                              </span>
                            )}
                          </td>
                          <td className="p-2.5 font-medium text-ink">{r.customerName || "—"}</td>
                          <td className="p-2.5 font-mono">{r.customerPhone || "—"}</td>
                          <td className="p-2.5 max-w-xs truncate" title={r.destAddress}>{r.destAddress || "—"}</td>
                          <td className="p-2.5 max-w-xs">
                            <div className="space-y-0.5">
                              {r.items.length > 1 ? (
                                <span className="block font-medium text-ink" title={r.productName}>
                                  {r.items.length} mặt hàng · {r.qty} cái
                                  <span className="block truncate font-normal text-muted">{r.productName}</span>
                                </span>
                              ) : (
                                <span className="block truncate font-medium text-ink" title={r.productName}>{r.productName}</span>
                              )}
                              {r.newProducts > 0 ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-warn/10 text-warn">
                                  {r.items.length > 1 ? `${r.newProducts} sản phẩm mới` : "Sản phẩm mới"}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-ok/10 text-ok">Khớp kho</span>
                              )}
                            </div>
                          </td>
                          <td className="p-2.5 text-right font-mono whitespace-nowrap">
                            {(r.weightGram ?? 0) >= 1000 ? `${((r.weightGram ?? 0) / 1000).toLocaleString("vi-VN")} kg` : `${r.weightGram ?? 0} g`}
                            {r.length && r.width && r.height ? (
                              <span className="block text-muted">{[r.length, r.width, r.height].map((mm) => (mm / 10).toLocaleString("vi-VN")).join("×")} cm</span>
                            ) : null}
                          </td>
                          <td className="p-2.5 text-right font-mono">{money(r.cod || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          ) : (
            /* ═════════ STEP 2: LIVE PROGRESS TRACKING ═════════ */
            <div className="space-y-4">
              <div className="p-4 rounded-lg border border-ok/30 bg-ok/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm font-semibold text-ink">
                      Đã xử lý {importResults.filter((r) => r.success).length}/{importResults.length} đơn hàng
                    </p>
                    {importResults.some((r) => r.existing) && (
                      <p className="text-xs text-warn mt-0.5">
                        {importResults.filter((r) => r.existing).length} đơn có mã trùng đơn đã có từ trước — không tạo lại.
                      </p>
                    )}
                    <p className="text-xs text-muted mt-0.5">
                      Vận đơn đã được cấp mã: <b>{readyToPrintCount}</b> / {importResults.length} đơn
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={startPollingShipmentCodes}
                  className="text-xs gap-1.5"
                >
                   Cập nhật mã
                </Button>
              </div>

              {importResults.some((r) => r.shipPayment) && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warn/40 bg-warn/10 px-4 py-3 text-sm text-ink">
                  <span>
                    <b>{importResults.filter((r) => r.shipPayment).length}</b> đơn chờ chuyển tiền ship (tổng{" "}
                    <b className="font-mono">{money(importResults.reduce((a, r) => a + (r.shipPayment?.amount ?? 0), 0))}</b>) — chưa gửi hãng.
                  </span>
                  <Link to="/orders?status=cho-duyet" onClick={handleCloseModal} className="shrink-0 font-medium text-accent hover:underline">
                    Chọn đơn để chuyển gộp →
                  </Link>
                </div>
              )}

              {/* Progress Table */}
              <div className="border border-rule rounded-lg overflow-hidden max-h-80 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-paperSoft sticky top-0 border-b border-rule">
                    <tr className="text-left text-muted">
                      <th className={cn("p-2.5", INDEX_CELL)}>{INDEX_HEADER}</th>
                      <th className="p-2.5">Mã đơn</th>
                      <th className="p-2.5">Khách hàng</th>
                      <th className="p-2.5">Địa chỉ nhận</th>
                      <th className="p-2.5 text-right">COD</th>
                      <th className="p-2.5">Tiến trình / mã vận đơn</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-rule">
                    {importResults.map((item, idx) => (
                      <tr key={idx} className="hover:bg-paperSoft/50">
                        <td className={cn("p-2.5", INDEX_CELL)}>{idx + 1}</td>
                        <td className="p-2.5 font-mono font-semibold text-accent">{item.orderId}</td>
                        <td className="p-2.5">
                          <p className="font-medium text-ink">{item.customerName}</p>
                          <p className="text-xs text-muted font-mono">{item.customerPhone || "—"}</p>
                        </td>
                        <td className="p-2.5 max-w-xs truncate text-muted" title={item.destAddress}>
                          {item.destAddress}
                        </td>
                        <td className="p-2.5 text-right font-mono font-medium">{money(item.cod || 0)}</td>
                        <td className="p-2.5">
                          {item.existing ? (
                            <Link
                              to={`/orders/${encodeURIComponent(item.orderId)}`}
                              onClick={handleCloseModal}
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs bg-warn/10 text-warn font-medium hover:underline"
                            >
                              Đã có từ trước{item.shipmentCode ? ` (${item.shipmentCode})` : ""}
                            </Link>
                          ) : item.shipmentCode ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-ok/10 text-ok font-mono font-semibold text-xs border border-ok/20">
                               {item.shipmentCode}
                            </span>
                          ) : item.shipPayment ? (
                            <Link
                              to={`/orders/${encodeURIComponent(item.orderId)}`}
                              onClick={handleCloseModal}
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs bg-warn/10 text-warn font-medium hover:underline"
                            >
                              Chờ chuyển tiền ship {money(item.shipPayment.amount)}
                            </Link>
                          ) : !item.success ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs bg-danger/10 text-danger font-medium" title={item.error}>
                               Lỗi: {item.error || "Không tạo được"}
                            </span>
                          ) : item.sagaId ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs bg-accent/10 text-accent font-medium animate-pulse">
                              <Loader2 className="size-3 animate-spin" /> Đang cấp mã vận đơn...
                            </span>
                          ) : (
                            <span className="text-muted text-xs">Chưa tạo vận đơn</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-4 border-t border-rule bg-paperSoft/30 flex items-center justify-between">
          {step === "upload" ? (
            <>
              <Button variant="outline" onClick={handleCloseModal} disabled={importing}>
                Đóng
              </Button>
              {rows.length > 0 && (
                <Button onClick={handleImport} disabled={importing || readingColumns || validRows.length === 0 || pendingFeeChecks > 0}>
                  {importing ? "Đang tạo đơn…" : pendingFeeChecks > 0 ? "Đang kiểm tra cước…" : `Tạo ${validRows.length} đơn`}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleCloseModal}>
                Đóng
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => handleWaybills("download")} disabled={busy != null || readyToPrintCount === 0} className="gap-1.5">
                  <Download aria-hidden />
                  {busy === "download" ? "Đang tải…" : `Tải ${readyToPrintCount} vận đơn`}
                </Button>
                <Button onClick={() => handleWaybills("print")} disabled={busy != null || readyToPrintCount === 0} className="gap-1.5">
                  <Printer aria-hidden />
                  {busy === "print" ? "Đang chuẩn bị…" : `In ${readyToPrintCount} vận đơn`}
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
